import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    file: null,
    loading: false,
    error: null,
    successMessage: null,
    resultData: null,
    // Phase 1: Single evaluation status tracking
    evaluationStatus: 'idle', // 'idle' | 'processing' | 'completed' | 'error'
    // Evaluation history (ViewEvaluations page)
    evaluationHistory: {
      evaluations: [],
      pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
      assessments: [],
      loading: false,
      error: null
    },
    // Answer sheet validation
    validation: {
      status: 'idle', // 'idle' | 'validating' | 'completed' | 'error'
      result: null,
      error: null
    },
    bulkValidation: {
      status: 'idle', // 'idle' | 'validating' | 'completed' | 'error'
      results: null,
      summary: null,
      error: null
    },
    // Phase 2: Bulk evaluation state
    bulkEvaluation: {
      jobId: null,
      status: 'idle', // 'idle' | 'uploading' | 'processing' | 'completed' | 'error'
      totalFiles: 0,
      processedFiles: 0,
      successCount: 0,
      failedCount: 0,
      files: [],
      results: null,
      error: null,
      // Student-level tally + the short batch reference the server logs under.
      expectedStudents: 0,
      gradedStudents: 0,
      jobRef: null,
      // Job-level refusal reason (grouping contract broken → nothing graded).
      jobError: null
    }
  };

const evaluationViewSlice = createSlice({
  name: 'viewEvaluation',
  initialState,
  reducers: {
    uploadFileStart: (state) => {
      state.loading = true;
      state.error = null;
      state.successMessage = null;
    },
    uploadFileSuccess: (state, action) => {
      state.loading = false;
      state.resultData = action.payload;
      state.successMessage = 'Answer evaluated successfully!';
    },
    // Silent in-place refresh of the open evaluation — used after a report-card
    // edit (score / answer / feedback). Deliberately does NOT touch `loading`,
    // so EvaluationReport keeps the question list mounted instead of swapping to
    // the full-page spinner: the teacher's scroll position and context are
    // preserved while the recalculated total + recomputed strengths/feedback
    // flow back in (2026-05-31, report-card editing UAT).
    refreshResultData: (state, action) => {
      state.resultData = action.payload;
    },
    uploadFileFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.successMessage = null;
    },
    loadingTrue: (state) => {
      state.loading = true;
    },
    loadingFalse: (state) => {
      state.loading = false;
    },
    // Phase 1: Evaluation status management
    setEvaluationStatus: (state, action) => {
      state.evaluationStatus = action.payload;
    },
    resetEvaluation: (state) => {
      state.evaluationStatus = 'idle';
      state.resultData = null;
      state.error = null;
      state.successMessage = null;
      state.loading = false;
    },
    // Evaluation history reducers
    setEvaluationHistoryLoading: (state, action) => {
      state.evaluationHistory.loading = action.payload;
      if (action.payload) state.evaluationHistory.error = null;
    },
    setEvaluationHistoryData: (state, action) => {
      state.evaluationHistory.evaluations = action.payload.evaluations;
      state.evaluationHistory.pagination = action.payload.pagination;
      state.evaluationHistory.assessments = action.payload.assessments;
      state.evaluationHistory.loading = false;
      state.evaluationHistory.error = null;
    },
    setEvaluationHistoryError: (state, action) => {
      state.evaluationHistory.loading = false;
      state.evaluationHistory.error = action.payload;
    },
    // Answer sheet validation reducers
    setValidationStart: (state) => {
      state.validation.status = 'validating';
      state.validation.result = null;
      state.validation.error = null;
    },
    setValidationResult: (state, action) => {
      state.validation.status = 'completed';
      state.validation.result = action.payload;
      state.validation.error = null;
    },
    setValidationError: (state, action) => {
      state.validation.status = 'error';
      state.validation.error = action.payload;
    },
    resetValidation: (state) => {
      state.validation = { status: 'idle', result: null, error: null };
    },
    setBulkValidationStart: (state) => {
      state.bulkValidation.status = 'validating';
      state.bulkValidation.results = null;
      state.bulkValidation.summary = null;
      state.bulkValidation.error = null;
    },
    setBulkValidationResult: (state, action) => {
      state.bulkValidation.status = 'completed';
      state.bulkValidation.results = action.payload.results;
      state.bulkValidation.summary = action.payload.summary;
      state.bulkValidation.error = null;
    },
    setBulkValidationError: (state, action) => {
      state.bulkValidation.status = 'error';
      state.bulkValidation.error = action.payload;
    },
    resetBulkValidation: (state) => {
      state.bulkValidation = { status: 'idle', results: null, summary: null, error: null };
    },
    // Phase 2: Bulk evaluation reducers
    setBulkJobStarted: (state, action) => {
      state.bulkEvaluation.jobId = action.payload.jobId;
      state.bulkEvaluation.totalFiles = action.payload.totalFiles;
      state.bulkEvaluation.status = 'processing';
      state.bulkEvaluation.processedFiles = 0;
      state.bulkEvaluation.successCount = 0;
      state.bulkEvaluation.failedCount = 0;
      state.bulkEvaluation.files = [];
      state.bulkEvaluation.results = null;
      state.bulkEvaluation.error = null;
      // Cleared explicitly: a previous batch's refusal must not haunt the new
      // one's progress screen.
      state.bulkEvaluation.jobError = null;
      state.bulkEvaluation.jobRef = action.payload.jobRef || null;
      state.bulkEvaluation.expectedStudents = action.payload.expectedStudents || 0;
      state.bulkEvaluation.gradedStudents = 0;
    },
    setBulkUploading: (state) => {
      state.bulkEvaluation.status = 'uploading';
      state.bulkEvaluation.error = null;
    },
    setBulkProgress: (state, action) => {
      const {
        status, totalFiles, processedFiles, successCount, failedCount, files,
        expectedStudents, gradedStudents, jobRef, jobError,
      } = action.payload;
      state.bulkEvaluation.status = status === 'in_progress' ? 'processing' : status;
      state.bulkEvaluation.totalFiles = totalFiles;
      state.bulkEvaluation.processedFiles = processedFiles;
      state.bulkEvaluation.successCount = successCount ?? state.bulkEvaluation.successCount;
      state.bulkEvaluation.failedCount = failedCount ?? state.bulkEvaluation.failedCount;
      // Progress is counted in STUDENTS as well as pages — a 5-student batch
      // reporting "22" was one of the tells that pages were being graded as
      // students (2026-07-29).
      state.bulkEvaluation.expectedStudents = expectedStudents ?? state.bulkEvaluation.expectedStudents;
      state.bulkEvaluation.gradedStudents = gradedStudents ?? state.bulkEvaluation.gradedStudents;
      state.bulkEvaluation.jobRef = jobRef ?? state.bulkEvaluation.jobRef;
      // A contract violation fails the whole job with no per-file errors to
      // show. Without this the teacher got a bare "failed" and an empty list.
      if (jobError !== undefined) state.bulkEvaluation.jobError = jobError;
      if (files) {
        state.bulkEvaluation.files = files;
      }
    },
    setBulkResults: (state, action) => {
      state.bulkEvaluation.results = action.payload;
      state.bulkEvaluation.status = 'completed';
    },
    setBulkError: (state, action) => {
      state.bulkEvaluation.status = 'error';
      state.bulkEvaluation.error = action.payload;
    },
    resetBulkEvaluation: (state) => {
      state.bulkEvaluation = { ...initialState.bulkEvaluation };
    }
  },
});

export const {
  uploadFileStart,
  uploadFileSuccess,
  refreshResultData,
  uploadFileFailure,
  loadingTrue,
  loadingFalse,
  setEvaluationStatus,
  resetEvaluation,
  setEvaluationHistoryLoading,
  setEvaluationHistoryData,
  setEvaluationHistoryError,
  setValidationStart,
  setValidationResult,
  setValidationError,
  resetValidation,
  setBulkValidationStart,
  setBulkValidationResult,
  setBulkValidationError,
  resetBulkValidation,
  setBulkJobStarted,
  setBulkUploading,
  setBulkProgress,
  setBulkResults,
  setBulkError,
  resetBulkEvaluation
} = evaluationViewSlice.actions;

export default evaluationViewSlice.reducer;
